import { describeInteraction, formatClockTime } from 'utils/error-log/format';
import { parseTriggerAction } from 'utils/error-log/parse-error-log-blobs';

type ErrorLogTriggerViewProps = {
  trigger: unknown;
};

export default function ErrorLogTriggerView({
  trigger
}: ErrorLogTriggerViewProps) {
  const parsed = parseTriggerAction(trigger);
  if (!parsed) {
    return (
      <div className='text-sm text-gray-400'>No trigger context recorded.</div>
    );
  }

  const { lastInteraction, recentInteractions, recentReduxActions } = parsed;

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-col gap-1'>
        <div className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
          Last interaction before the error
        </div>
        {lastInteraction ? (
          <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-gray-900'>
            <div className='font-medium'>
              {describeInteraction(lastInteraction)}
            </div>
            <div className='mt-1 break-all font-mono text-xs text-gray-600'>
              {lastInteraction.target.path}
            </div>
            <div className='mt-1 text-xs text-gray-500'>
              {formatClockTime(lastInteraction.timestamp)}
            </div>
          </div>
        ) : (
          <div className='text-sm text-gray-400'>
            No user interaction recorded.
          </div>
        )}
      </div>

      <div className='grid grid-cols-1 gap-5 lg:grid-cols-2'>
        <div className='flex flex-col gap-1'>
          <div className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Recent interactions
          </div>
          {recentInteractions.length === 0 ? (
            <div className='text-sm text-gray-400'>None.</div>
          ) : (
            <ol className='divide-y divide-gray-200 rounded-md border border-gray-200 bg-white text-sm'>
              {recentInteractions.map((interaction) => (
                <li
                  key={interaction.id}
                  className='flex flex-col gap-0.5 px-3 py-2'
                >
                  <div className='flex items-baseline justify-between gap-3'>
                    <span className='text-gray-900'>
                      {describeInteraction(interaction)}
                    </span>
                    <span className='shrink-0 text-xs text-gray-500'>
                      {formatClockTime(interaction.timestamp)}
                    </span>
                  </div>
                  <span className='break-all font-mono text-xs text-gray-500'>
                    {interaction.target.path}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className='flex flex-col gap-1'>
          <div className='text-xs font-semibold uppercase tracking-wide text-gray-500'>
            Recent Redux actions
          </div>
          {recentReduxActions.length === 0 ? (
            <div className='text-sm text-gray-400'>None.</div>
          ) : (
            <ol className='divide-y divide-gray-200 rounded-md border border-gray-200 bg-white text-sm'>
              {recentReduxActions.map((action) => (
                <li
                  key={action.id}
                  className='flex items-baseline justify-between gap-3 px-3 py-2'
                >
                  <span className='break-all font-mono text-xs text-gray-900'>
                    {action.type}
                  </span>
                  <span className='shrink-0 text-xs text-gray-500'>
                    {formatClockTime(action.timestamp)}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      </div>
    </div>
  );
}
